/**
 * AI 任务监控页 (B1)
 *
 * Phase 3-1 / Plan §5.4 B1 / Design §8.2 ai_task 权限点。
 *
 * 权限：
 *   - AI_TASK_READ  — 查看列表、详情、状态分布
 *   - AI_TASK_RETRY — 重试失败任务
 *   - AI_TASK_CANCEL — 取消进行中任务
 *
 * 无 AI_TASK_READ 时渲染 403 提示。
 */
import { useState } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Form,
  Select,
  Input,
  DatePicker,
  Row,
  Col,
  Statistic,
  Card,
  Drawer,
  Descriptions,
  Modal,
  message,
  Typography,
  Result,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  listAiTasks,
  retryAiTask,
  cancelAiTask,
  type AiTaskDto,
  type AiTaskStatus,
  type ListAiTasksFilter,
} from '../../api/ai-tasks';
import { PermissionGate } from '../../components/PermissionGate';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

/** Status Tag 颜色映射 */
const STATUS_COLOR: Record<AiTaskStatus, string> = {
  done: 'green',
  pending: 'blue',
  processing: 'gold',
  failed: 'red',
  cancelled: 'default',
};

/** Status 中文映射 */
const STATUS_LABEL: Record<AiTaskStatus, string> = {
  done: '完成',
  pending: '待处理',
  processing: '处理中',
  failed: '失败',
  cancelled: '已取消',
};

/**
 * 格式化 JSON 字段：
 *   - null / undefined → '-'
 *   - 合法 JSON 字符串 → 缩进格式
 *   - 非 JSON 字符串 → 原始字符串
 */
function formatJsonField(value: unknown): string {
  if (value === null || value === undefined) return '-';
  // 生产库 input_ref/output_ref 是 MySQL JSON 列，驱动会把它解析成对象/数组再原样透传，
  // 并非 DTO 类型声明的 string。直接当 React 子节点渲染会抛「Objects are not valid as a React child」。
  if (typeof value !== 'string') {
    try {
      return JSON.stringify(value, null, 2) ?? String(value);
    } catch {
      return String(value);
    }
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/** 过滤参数（受控） */
interface QueryParams extends ListAiTasksFilter {
  page: number;
  pageSize: number;
}

const DEFAULT_PARAMS: QueryParams = {
  page: 1,
  pageSize: 20,
  limit: 20,
  offset: 0,
};

export default function AiTasksPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.AI_TASK_READ}
      fallback={
        <Result status="403" title="403" subTitle="无权访问 AI 任务监控" />
      }
    >
      <AiTasksPageInner />
    </PermissionGate>
  );
}

function AiTasksPageInner() {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [queryParams, setQueryParams] = useState<QueryParams>(DEFAULT_PARAMS);

  // Drawer 状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AiTaskDto | null>(null);

  // Cancel Modal 状态
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTask, setCancelTask] = useState<AiTaskDto | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // 权限
  const canRetry = usePermission(PERMISSIONS.AI_TASK_RETRY);
  const canCancel = usePermission(PERMISSIONS.AI_TASK_CANCEL);

  // 列表查询（含 statusDistribution piggyback）
  const { data, isLoading } = useQuery({
    queryKey: ['ai-tasks', queryParams],
    queryFn: () =>
      listAiTasks({
        status: queryParams.status,
        taskType: queryParams.taskType,
        workId: queryParams.workId,
        dialogueId: queryParams.dialogueId,
        startAt: queryParams.startAt,
        endAt: queryParams.endAt,
        limit: queryParams.pageSize,
        offset: (queryParams.page - 1) * queryParams.pageSize,
      }),
    refetchInterval: 30_000,
  });

  const dist = data?.statusDistribution;

  // ——— 操作 ———

  function handleSearch() {
    const vals = form.getFieldsValue();
    const [startAt, endAt] = vals.dateRange
      ? [
          dayjs(vals.dateRange[0]).toISOString(),
          dayjs(vals.dateRange[1]).toISOString(),
        ]
      : [undefined, undefined];
    setQueryParams({
      ...DEFAULT_PARAMS,
      status: vals.status || undefined,
      taskType: vals.taskType || undefined,
      workId: vals.workId || undefined,
      dialogueId: vals.dialogueId || undefined,
      startAt,
      endAt,
    });
  }

  function handleReset() {
    form.resetFields();
    setQueryParams(DEFAULT_PARAMS);
  }

  async function handleRetry(task: AiTaskDto) {
    try {
      await retryAiTask(task.taskId);
      message.success('重试任务已提交');
      queryClient.invalidateQueries({ queryKey: ['ai-tasks'] });
    } catch {
      message.error('重试失败，请稍后再试');
    }
  }

  function openCancelModal(task: AiTaskDto) {
    setCancelTask(task);
    setCancelReason('');
    setCancelModalOpen(true);
  }

  async function handleCancelConfirm() {
    if (!cancelTask) return;
    if (cancelReason.length < 5 || cancelReason.length > 200) {
      message.error('取消原因需 5~200 字符');
      return;
    }
    setCancelLoading(true);
    try {
      await cancelAiTask(cancelTask.taskId, cancelReason);
      message.success('任务已取消');
      setCancelModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ai-tasks'] });
    } catch {
      message.error('取消失败，请稍后再试');
    } finally {
      setCancelLoading(false);
    }
  }

  // ——— 表格列 ———

  const columns: ColumnsType<AiTaskDto> = [
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string | null) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '任务 ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: 200,
      ellipsis: true,
    },
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 160,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: AiTaskStatus) => (
        <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
      ),
    },
    {
      title: '作品 ID',
      dataIndex: 'workId',
      key: 'workId',
      width: 200,
      ellipsis: true,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: '重试次数',
      dataIndex: 'retryCount',
      key: 'retryCount',
      width: 80,
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_: unknown, task: AiTaskDto) => (
        <Space>
          <Button
            size="small"
            data-testid={`ai-tasks-detail-btn-${task.taskId}`}
            onClick={() => {
              setSelectedTask(task);
              setDrawerOpen(true);
            }}
          >
            详情
          </Button>
          {task.status === 'failed' && (
            <Button
              size="small"
              type="primary"
              disabled={!canRetry}
              data-testid={`ai-tasks-retry-btn-${task.taskId}`}
              onClick={() => handleRetry(task)}
            >
              重试
            </Button>
          )}
          {(task.status === 'pending' || task.status === 'processing') && (
            <Button
              size="small"
              danger
              disabled={!canCancel}
              data-testid={`ai-tasks-cancel-btn-${task.taskId}`}
              onClick={() => openCancelModal(task)}
            >
              取消
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="ai-tasks-page">
      {/* 状态分布统计 */}
      <div data-testid="ai-tasks-status-distribution">
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic title="待处理 (pending)" value={dist?.pending ?? '-'} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="处理中 (processing)" value={dist?.processing ?? '-'} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="已完成 (done)" value={dist?.done ?? '-'} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="失败 (failed)" value={dist?.failed ?? '-'} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="已取消 (cancelled)" value={dist?.cancelled ?? '-'} />
            </Card>
          </Col>
        </Row>
      </div>

      {/* 过滤表单 */}
      <Form
        form={form}
        layout="inline"
        data-testid="ai-tasks-filter-form"
        style={{ marginBottom: 16 }}
        onFinish={handleSearch}
      >
        <Form.Item name="status">
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 130 }}
            options={[
              { value: 'pending', label: '待处理' },
              { value: 'processing', label: '处理中' },
              { value: 'done', label: '完成' },
              { value: 'failed', label: '失败' },
              { value: 'cancelled', label: '已取消' },
            ]}
          />
        </Form.Item>
        <Form.Item name="taskType">
          <Input placeholder="任务类型" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="workId">
          <Input placeholder="作品 ID" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="dialogueId">
          <Input placeholder="对话 ID" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="dateRange">
          <RangePicker showTime />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            data-testid="ai-tasks-search"
          >
            查询
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            data-testid="ai-tasks-reset"
            onClick={handleReset}
          >
            重置
          </Button>
        </Form.Item>
      </Form>

      {/* 列表 */}
      <Table<AiTaskDto>
        rowKey="taskId"
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isLoading}
        pagination={{
          current: queryParams.page,
          pageSize: queryParams.pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          onChange: (page, pageSize) =>
            setQueryParams((p) => ({ ...p, page, pageSize, limit: pageSize, offset: (page - 1) * pageSize })),
        }}
      />

      {/* 详情 Drawer */}
      <Drawer
        title="AI 任务详情"
        width={720}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data-testid="ai-tasks-detail-drawer"
      >
        {selectedTask && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="任务 ID" span={2}>
              {selectedTask.taskId}
            </Descriptions.Item>
            <Descriptions.Item label="任务类型">
              {selectedTask.taskType}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_COLOR[selectedTask.status]}>
                {STATUS_LABEL[selectedTask.status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="作品 ID">
              {selectedTask.workId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="对话 ID">
              {selectedTask.dialogueId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="重试次数">
              {selectedTask.retryCount}
            </Descriptions.Item>
            <Descriptions.Item label="模型">
              {selectedTask.modelName ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="幂等键" span={2}>
              {selectedTask.idempotencyKey ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Prompt 模板 ID" span={2}>
              {selectedTask.promptTemplateId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="输入引用" span={2}>
              <Typography.Text code style={{ whiteSpace: 'pre-wrap' }}>
                {formatJsonField(selectedTask.inputRef)}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="输出引用" span={2}>
              <Typography.Text code style={{ whiteSpace: 'pre-wrap' }}>
                {formatJsonField(selectedTask.outputRef)}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="错误信息" span={2}>
              {selectedTask.errorMessage ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="取消原因" span={2}>
              {selectedTask.cancelReason ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {selectedTask.createdAt
                ? dayjs(selectedTask.createdAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {selectedTask.updatedAt
                ? dayjs(selectedTask.updatedAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="完成时间">
              {selectedTask.completedAt
                ? dayjs(selectedTask.completedAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="取消时间">
              {selectedTask.cancelledAt
                ? dayjs(selectedTask.cancelledAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* 取消 Modal */}
      <Modal
        title="取消任务"
        open={cancelModalOpen}
        onOk={handleCancelConfirm}
        onCancel={() => setCancelModalOpen(false)}
        confirmLoading={cancelLoading}
        data-testid="ai-tasks-cancel-modal"
      >
        <p>请输入取消原因（5~200 字符）：</p>
        <TextArea
          rows={4}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="请输入取消原因（5~200 字符）"
        />
      </Modal>
    </div>
  );
}